import React from "react";
import { Instagram, Facebook, MessageCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

// X / Twitter logo — lucide-react's `Twitter` is the legacy bird, so render
// our own minimal X mark in an inline SVG to match Meta/Apple style guides.
function XMark({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M18.244 2H21l-6.55 7.49L22 22h-6.81l-4.74-6.21L4.95 22H2.19l7.01-8.02L2 2h6.91l4.27 5.65L18.244 2zm-2.39 18h1.85L7.21 4H5.26L15.854 20z" />
    </svg>
  );
}

/**
 * Renders the 4 social-icon row pulled from theme settings. Each icon is
 * only rendered when its URL is non-empty, so admins can enable / disable
 * any combination by editing the URL fields in Admin → Theme.
 *
 * Used on the homepage opposite the "LAteST from LAST" headline; sized
 * appropriately for desktop and reflows to a clean right-aligned row on
 * mobile. WhatsApp opens the configured Channel link.
 */
export default function SocialLinks({ className = "", large = false }) {
  const { theme } = useTheme();
  const items = [
    { id: "instagram", url: theme.social_instagram_url, label: "Instagram",
      Icon: Instagram, bg: "#E1306C" },
    { id: "facebook",  url: theme.social_facebook_url, label: "Facebook",
      Icon: Facebook, bg: "#1877F2" },
    { id: "x",         url: theme.social_x_url, label: "X (Twitter)",
      Icon: XMark, bg: "#000000" },
    { id: "whatsapp",  url: theme.social_whatsapp_url, label: "WhatsApp Channel",
      Icon: MessageCircle, bg: "#25D366" },
  ].filter((it) => (it.url || "").trim().length > 0);

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Follow LAST on social media"
      data-testid="social-links"
      className={`flex flex-wrap items-center justify-end gap-1 sm:gap-2 ${className}`}
    >
      {items.map((it) => (
        <a
          key={it.id}
          href={it.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={it.label}
          title={it.label}
          data-testid={`social-${it.id}`}
          style={{ backgroundColor: it.bg }}
          className={`inline-flex items-center justify-center rounded-full text-white transition-transform hover:-translate-y-0.5 hover:opacity-90 sm:h-10 sm:w-10 ${
            large ? "h-11 w-11" : "h-6 w-6"
          }`}
        >
          <it.Icon size={large ? 20 : 18} />
        </a>
      ))}
    </nav>
  );
}
